import { AiOutlineCheckCircle, AiOutlineExclamationCircle } from 'react-icons/ai'
import styled from '@emotion/styled'
import { BACKGROUND, ORANGE, WHITE } from '../constant/colors'
import Text from '../components/common/Text'
import Button from '../components/common/Button'
import toast, { Toaster } from 'react-hot-toast'
import { mobile, useScreen } from '../utils/responsive-helper'
import { CouponDetail } from '../services/coupon/coupon-typed'
import dayjs from 'dayjs'
import { useCollectCoupon } from '../services/coupon/coupon-query'

const Layout = styled.div``

const CardStyle = styled.div`
  display: flex-root;
  width: 100%;
  height: 148px;
  margin-bottom: 24px;
  background: linear-gradient(270deg, #ffb292 0%, ${ORANGE} 100%);
  border-radius: 4px;
`

const DotLeftStyle = styled.div`
  position: relative;
  top: calc(50% - 8px);
  left: -8px;
  width: 16px;
  height: 16px;
  background: ${BACKGROUND};
  border-radius: 50%;
  display: inline-flex;
`

const DotRightStyle = styled.div`
  position: relative;
  top: calc(50% - 8px);
  left: calc(100% - 8px - 16px);
  width: 16px;
  height: 16px;
  background: ${BACKGROUND};
  border-radius: 50%;
  display: inline-flex;
`

const ContentStyle = styled.div`
  width: 100%;
  display: flex;
  padding: 0 16px;
  flex-direction: column;
  justify-content: center;
`

const HeaderStyle = styled.div`
  display: flex;
  justify-content: space-between;
`

const HeaderTextStyle = styled(Text)`
  font-size: 28px;
  line-height: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 372px;
`

const BodyStyle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
`

const DetailStyle = styled.div``

const DetailTextStyle = styled(Text)`
  font-size: 16px;
  line-height: 18px;
  margin-bottom: 8px;
`

const CouponDetailStyle = styled.div``

const DetailSectionStyle = styled.div`
  padding-bottom: 16px;
`

const HeaderText = styled(Text)`
  font-size: 20px;
  line-height: 22px;

  ${mobile} {
    font-size: 18px;
    line-height: 22px;
  }
`

const ConfirmButton = styled(Button)`
  max-width: none;
  margin-bottom: 16px;
`

const SuccessToastStyle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  color: ${WHITE};
  background: #53b64b;
  border-radius: 4px;
  padding: 8px;
`

const FailToastStyle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  color: ${WHITE};
  background: #c11d1a;
  border-radius: 4px;
  padding: 8px;
`

const ContentToastStyle = styled(Text)`
  color: ${WHITE};
  padding: 0 24px 0 8px;
`

const UseCouponToastStyle = styled(Text)`
  color: ${WHITE};
  padding: 0;
  padding-right: 8px;
`

type CouponInformationProps = {
  couponDetail?: CouponDetail
  buttonText?: string
  onClickButton?: () => void
}

const CouponInformation = (props: CouponInformationProps) => {
  const { isMobile } = useScreen()
  const { couponDetail, buttonText, onClickButton } = props
  const { mutate: collectCoupon } = useCollectCoupon()

  const dateFormatter = (time?: number) => {
    if (!time) {
      return
    }

    return dayjs(time).format('DD/MM/YYYY')
  }

  const notify = () => {
    if (couponDetail?.id) {
      collectCoupon(couponDetail.id, {
        onSuccess: () => {
          showToast(true)
        },
        onError: (error) => {
          showToast(false, error.message)
        },
      })
    }
  }

  const showToast = (success: boolean, message?: string) => {
    if (success) {
      toast.custom(() => (
        <SuccessToastStyle>
          <AiOutlineCheckCircle />
          <ContentToastStyle>คุณได้รับคูปองแล้ว</ContentToastStyle>
          <UseCouponToastStyle size={14}>ใช้คูปองเลย</UseCouponToastStyle>
        </SuccessToastStyle>
      ))
    } else {
      toast.custom(() => (
        <FailToastStyle>
          <AiOutlineExclamationCircle />
          <ContentToastStyle>{message || 'something went wrong'}</ContentToastStyle>
        </FailToastStyle>
      ))
    }
  }

  return (
    <Layout className="coupon-information">
      <div>
        <CardStyle>
          <DotLeftStyle />
          <DotRightStyle />
          <ContentStyle>
            <HeaderStyle>
              <HeaderTextStyle color={WHITE} type="Bold">
                {couponDetail?.title}
              </HeaderTextStyle>
            </HeaderStyle>
            <BodyStyle>
              <DetailStyle>
                <DetailTextStyle
                  color={WHITE}
                >{`Min. spend ฿${couponDetail?.minSpend}`}</DetailTextStyle>
                <DetailTextStyle color={WHITE}>{`Valid until ${dateFormatter(
                  couponDetail?.endDate,
                )}`}</DetailTextStyle>
              </DetailStyle>
            </BodyStyle>
          </ContentStyle>
        </CardStyle>
        <CouponDetailStyle>
          <DetailSectionStyle>
            <HeaderText type="SemiBold">คูปอง</HeaderText>
            <Text>{couponDetail?.title}</Text>
          </DetailSectionStyle>
          <DetailSectionStyle>
            <HeaderText type="SemiBold">เวลาที่ใช้ได้</HeaderText>
            <Text>
              {`${dateFormatter(couponDetail?.startDate)} - ${dateFormatter(
                couponDetail?.endDate,
              )}`}
            </Text>
          </DetailSectionStyle>
          <DetailSectionStyle>
            <HeaderText type="SemiBold">อุปกรณ์</HeaderText>
            <Text>{couponDetail?.devices}</Text>
          </DetailSectionStyle>
          <DetailSectionStyle>
            <HeaderText type="SemiBold">การชำระเงิน</HeaderText>
            <Text>{couponDetail?.paymentType}</Text>
          </DetailSectionStyle>
          <DetailSectionStyle>
            <HeaderText type="SemiBold">รายละเอียดเพิ่มเติม</HeaderText>
            <Text>{couponDetail?.description}</Text>
          </DetailSectionStyle>
          <Toaster position={isMobile ? 'bottom-center' : 'top-center'} />
        </CouponDetailStyle>
      </div>
      <ConfirmButton onClick={onClickButton || notify}>{buttonText || 'ตกลง'}</ConfirmButton>
    </Layout>
  )
}

export default CouponInformation
